import { useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { gameProfileKey, type ThemeGame } from '@/lib/themeGames';
import { submitGameProfile } from '@/lib/api';
import type { GameAlert } from '@/lib/chargingGameAlerts';
import styles from './ChargingGameOverlay.module.css';

/** Live session numbers pushed to the game on every poll tick. */
export interface GameSessionPayload {
  kwh: number;
  /** Pre-formatted for display — the game renders it verbatim. */
  cost: string;
  /** mm:ss (h:mm:ss past the hour). */
  elapsed: string;
}

/**
 * Parent-owned chrome above the game: the session numbers the driver
 * must never lose sight of, and the way back. Present on the charging
 * host, absent in the arcade. Ruling (frictionless flow, piece 2): a
 * game is opt-in, but the numbers have to survive that choice — nobody
 * should have to close a game to see what a charge is costing. Stop is
 * NOT here (Warren, 2026-09-16): a Stop pill beside the close control
 * got hit by mistake, and ending a charge is the one tap that must
 * never be an accident. The X returns to the charging page, where End
 * Session lives behind its confirm. The strip is TOP-anchored under
 * the status bar so the home indicator can never eat it.
 */
export interface GameStrip {
  items: Array<{ label: string; value: string }>;
}

interface Props {
  game: ThemeGame;
  session: GameSessionPayload;
  /** Non-null pauses the game and shows the reason. */
  alert: GameAlert | null;
  /** Resolves an alert's messageKey to display copy. */
  alertText: (a: GameAlert) => string;
  onClose: () => void;
  /** Fired on finish/gameover so the leaderboard work can pick it up. */
  onResult?: (result: Record<string, unknown>) => void;
  title: string;
  closeLabel: string;
  /**
   * Sessionless (arcade) mode. When present, `ready` posts
   * `cmd 'arcade' { rank, points }` INSTEAD of session data and the
   * session re-post effect is disabled — the host has no live session
   * to feed. The charging path never passes this, so its behavior is
   * byte-identical to before the arcade existed.
   */
  arcade?: { rank: number | null; points: number };
  /** Live-session chrome above the frame; omit for the arcade. */
  strip?: GameStrip;
}

/**
 * Full-screen host for a theme's mini-game.
 *
 * MOUNTING. The iframe is created when this component mounts and
 * destroyed when it unmounts — the caller renders `<ChargingGameOverlay>`
 * only while open, so the game is loaded lazily on first open and torn
 * down on close. Nothing keeps running behind the session UI.
 *
 * MESSAGE DISCIPLINE. Outbound messages carry `{ target: ns, ... }`,
 * inbound must carry `{ source: ns, ... }`. Anything else is ignored:
 * the frame is same-origin, but `window` receives messages from every
 * frame and browser extension on the page, so the namespace is what
 * stops foreign traffic being read as game state. The origin is checked
 * too — a same-origin bundle has no reason to accept cross-origin posts.
 *
 * The overlay deliberately owns no game state. Score, unlocks and best
 * runs live in the game; the parent only persists the opaque profile
 * blob it is handed back.
 */
export function ChargingGameOverlay({
  game, session, alert, alertText, onClose, onResult, title, closeLabel, arcade, strip,
}: Props) {
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const readyRef = useRef(false);
  // Latest values, read inside the message handler without making it a
  // dependency (re-subscribing on every poll tick would drop messages).
  const sessionRef = useRef(session);
  sessionRef.current = session;
  const arcadeRef = useRef(arcade);
  arcadeRef.current = arcade;
  // The games are standalone HTML bundles that cannot import the
  // app's i18n — each is TOLD its locale via cmd 'locale' instead.
  // i18n.language holds the app's supported locale codes exactly
  // (en/es/fr/hy/it/ja/ko/pt/ru/uk/zh); useTranslation re-renders on
  // language change, so the effect below re-posts mid-game switches.
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const langRef = useRef(lang);
  langRef.current = lang;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;

  const post = useCallback((cmd: string, extra?: Record<string, unknown>): void => {
    const win = frameRef.current?.contentWindow;
    if (!win) return;
    win.postMessage({ target: game.ns, cmd, ...extra }, window.location.origin);
  }, [game.ns]);

  // ── Inbound ────────────────────────────────────────────────────────
  useEffect(() => {
    function onMessage(e: MessageEvent): void {
      if (e.origin !== window.location.origin) return;
      const data = e.data as { source?: string; type?: string } | null;
      if (!data || typeof data !== 'object') return;
      if (data.source !== game.ns) return;

      switch (data.type) {
        case 'ready': {
          readyRef.current = true;
          // Restore the saved profile, then hand over the first session
          // numbers so the in-game strip is populated on first paint
          // rather than blank until the next poll tick.
          let profile: unknown = null;
          try {
            const raw = window.localStorage.getItem(gameProfileKey(game.ns));
            profile = raw ? JSON.parse(raw) : null;
          } catch {
            profile = null;
          }
          if (profile) post('profile', { profile });
          // The game's locale — standalone bundles can't import the
          // app's i18n, so each is told its language. Games without a
          // 'locale' handler ignore it.
          post('locale', { lang: langRef.current });
          // Arcade hosts have no live session: instead of feeding the
          // in-game session strip zeros, send the arcade payload
          // (rank + points) and let the game swap the strip. Games
          // that don't know cmd 'arcade' yet just ignore it and show
          // their default zeros — harmless.
          if (arcadeRef.current) post('arcade', arcadeRef.current);
          else post('session', { data: sessionRef.current });
          break;
        }
        case 'exit':
          onCloseRef.current();
          break;
        case 'profile': {
          const blob = (data as { profile?: unknown }).profile ?? null;
          try {
            window.localStorage.setItem(
              gameProfileKey(game.ns),
              JSON.stringify(blob),
            );
          } catch {
            // Private mode / quota. The game keeps its in-memory state;
            // only persistence across opens is lost, which is not worth
            // interrupting play for.
          }
          // Server sync feeds the friends game leaderboard. Games only
          // emit 'profile' when something changed (end of a run), so
          // no throttle is needed. Fire-and-forget by design.
          // Context comes from the SAME discriminator that switches
          // the ready-cmd between session data and the arcade
          // payload (arcadeRef), so the ranked claim can never drift
          // from what the overlay actually is.
          if (blob) submitGameProfile(game.ns, blob, arcadeRef.current ? 'arcade' : 'charging');
          break;
        }
        case 'finish':
        case 'gameover':
          onResultRef.current?.(data as unknown as Record<string, unknown>);
          break;
        default:
          break;
      }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [game.ns, post]);

  // ── Session updates ────────────────────────────────────────────────
  // Fires when the session VALUES change, not on a timer: the caller
  // memoises the payload on its metrics, and React Query re-renders on
  // cache change. Since sessionEventStream invalidates
  // ['active-sessions'] on SSE events, a push reaches the game
  // immediately even while the poll interval sits at its 60s
  // stream-open value.
  //
  // Before 'ready' the frame has no listener, so the 'ready' handler
  // sends the first payload instead of this effect racing it.
  useEffect(() => {
    if (!readyRef.current) return;
    if (arcadeRef.current) return; // sessionless host — nothing to feed
    post('session', { data: session });
  }, [session, post]);

  // Arcade twin of the session re-post: the rank/points query can
  // resolve AFTER the driver opened a game (the board fetch races a
  // ~70KB game load), so re-post when the payload identity changes.
  // The host memoizes it on (rank, points), so this fires only on
  // real changes.
  useEffect(() => {
    if (!readyRef.current) return;
    if (!arcade) return;
    post('arcade', arcade);
  }, [arcade, post]);

  // Locale re-post: a driver changing language in Settings while a
  // game is open gets the game's strings to follow, same push
  // discipline as everything else.
  useEffect(() => {
    if (!readyRef.current) return;
    post('locale', { lang });
  }, [lang, post]);

  // ── Alerts ─────────────────────────────────────────────────────────
  // Sent whenever the derived alert CHANGES to a non-null value, so a
  // persisting condition doesn't re-pause the game on every 4s tick.
  const lastAlertRef = useRef<string | null>(null);
  useEffect(() => {
    const key = alert ? alert.reason : null;
    if (key === lastAlertRef.current) return;
    lastAlertRef.current = key;
    if (alert) post('alert', { message: alertText(alert) });
  }, [alert, alertText, post]);

  // ── Background / foreground ────────────────────────────────────────
  // The game has its own visibilitychange handler; this is the belt to
  // its braces, and harmless if it also pauses itself.
  useEffect(() => {
    function onVisibility(): void {
      post(document.hidden ? 'pause' : 'resume');
    }
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [post]);

  // Escape closes, matching every other full-screen surface in the app.
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onCloseRef.current();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return createPortal(
    <div className={styles.root} role="dialog" aria-modal="true" aria-label={title}>
      {/* Session chrome the game cannot cover: numbers left, the close
          control right — and only that. Every game bundle also draws
          its own LIVE strip from the `session` post; that in-frame copy
          is now redundant and can go bundle by bundle — this one is the
          contract. */}
      {strip && (
        <div className={styles.strip}>
          <dl className={styles.stripStats}>
            {strip.items.map((it, i) => (
              <div key={i} className={styles.stripStat}>
                <dt className={styles.stripLabel}>{it.label}</dt>
                <dd className={styles.stripValue}>{it.value}</dd>
              </div>
            ))}
          </dl>
          <div className={styles.stripActions}>
            <button
              type="button"
              className={styles.stripClose}
              onClick={onClose}
              aria-label={closeLabel}
            >
              ✕
            </button>
          </div>
        </div>
      )}
      <iframe
        ref={frameRef}
        className={styles.frame}
        src={game.src}
        title={title}
        // The game is first-party static content from our own origin.
        // allow-same-origin is required for it to reach localStorage and
        // for the origin check above to pass; scripts obviously. No
        // top-navigation, no popups, no forms.
        sandbox="allow-scripts allow-same-origin"
      />
      {/* Parent-owned exit (arcade, no strip). The game also emits
          'exit', but a game that fails to load or wedges would otherwise
          trap the driver on a full-screen surface. With a strip the
          close control lives in the strip instead. */}
      {!strip && (
        <button
          type="button"
          className={styles.close}
          onClick={onClose}
          aria-label={closeLabel}
        >
          ✕
        </button>
      )}
    </div>,
    document.body,
  );
}
